import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LEAD } from "@/constants/testIds/landing";
import { COURSE_LIST, CATEGORY_LABELS } from "@/data/courses";
import { trackLeadConversion } from "@/lib/analytics";
import { metaLead } from "@/lib/metaPixel";
import { getAttribution } from "@/lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  audience: "",
  course_interest: "",
  message: "",
  consent: false,
};

export default function LeadForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const apply = () => {
      try {
        const raw = sessionStorage.getItem("dh-lead-prefill");
        if (!raw) return;
        const data = JSON.parse(raw);
        sessionStorage.removeItem("dh-lead-prefill");
        setForm((f) => ({ ...f, ...data }));
      } catch { /* ignore */ }
    };
    apply();
    window.addEventListener("dh-lead-prefill", apply);
    return () => window.removeEventListener("dh-lead-prefill", apply);
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Bitte fülle Name, E-Mail und Telefon aus.");
      return;
    }
    if (!form.consent) {
      toast.error("Bitte stimme der Datenschutzerklärung zu.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/leads`, {
        ...form,
        audience: form.audience || null,
        course_interest: form.course_interest || null,
        message: form.message || null,
        attribution: getAttribution(),
      });
      setDone(true);
      trackLeadConversion(7);
      metaLead("startseite-kontakt");
      toast.success("Danke! Wir melden uns innerhalb von 24h bei dir.");
      setForm(initial);
    } catch (err) {
      console.error(err);
      toast.error("Ups, das hat nicht geklappt. Bitte versuche es nochmal oder ruf uns an.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="relative py-32 md:py-44 overflow-hidden" style={{ backgroundColor: "#F6E8DC" }}>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        {/* Left — pitch (warm editorial) */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-terra" />
            <span className="font-poster text-[11px] uppercase tracking-[0.3em] font-medium text-terra">
              Kostenlose Beratung
            </span>
          </div>
          <h2 className="font-magazine font-medium text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] text-sand-800 leading-[1.08]">
            Starte deine <em className="italic text-terra">Heldenreise.</em>
          </h2>
          <p className="mt-6 text-sand-600 text-[16px] leading-[1.75] max-w-xl">
            Wir hören zu, prüfen deinen Bildungsgutschein und finden gemeinsam den Kurs, der zu deinem Leben passt — kostenlos, ehrlich, ohne Druck.
          </p>

          <div className="mt-10 space-y-4 max-w-md">
            <a
              href="tel:+4921129273330"
              data-testid={LEAD.phoneCta}
              className="group flex items-center gap-4 border-b border-sand-800/15 hover:border-terra pb-4 transition-colors"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-terra/50 text-terra shrink-0">
                <Phone className="h-4 w-4" strokeWidth={1.6} />
              </span>
              <span className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.28em] text-sand-500 font-semibold">Telefon</div>
                <div className="font-magazine text-[1.35rem] text-sand-800 mt-0.5">0211 29273330</div>
              </span>
              <ArrowRight className="h-4 w-4 text-sand-400 group-hover:text-terra group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
            </a>

            <a
              href="https://wa.me/491622572545"
              target="_blank"
              rel="noreferrer"
              data-testid={LEAD.whatsappCta}
              className="group flex items-center gap-4 border-b border-sand-800/15 hover:border-terra pb-4 transition-colors"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-terra/50 text-terra shrink-0">
                <MessageCircle className="h-4 w-4" strokeWidth={1.6} />
              </span>
              <span className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.28em] text-sand-500 font-semibold">WhatsApp</div>
                <div className="font-magazine text-[1.35rem] text-sand-800 mt-0.5">0162 2572545</div>
                <div className="text-[12px] text-sand-500 mt-0.5 italic">Schnelle Antwort, wann es dir passt</div>
              </span>
              <ArrowRight className="h-4 w-4 text-sand-400 group-hover:text-terra group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
            </a>
          </div>

          <ul className="mt-10 space-y-2.5 text-sand-600 text-[13px]">
            {['100 % kostenlos & unverbindlich', 'Antwort innerhalb 24 h', 'Hilfe bei Bildungsgutschein-Antrag'].map((t) => (
              <li key={t} className="flex items-center gap-2.5">
                <span className="inline-block h-[3px] w-4 bg-terra shrink-0" />
                {t}
              </li>
            ))}
          </ul>

          <p className="mt-12 font-magazine italic text-[1.15rem] text-terra max-w-md leading-relaxed hidden lg:block">
            „Pflege trägt Menschen durchs Leben. Wir tragen die, die pflegen."
          </p>
        </div>

        {/* Right — form (warm editorial panel) */}
        <div className="bg-white/85 text-sand-800 p-8 md:p-10 lg:p-12 border border-sand-800/10 border-t-[3px] border-t-terra rounded-2xl shadow-[0_32px_70px_-40px_rgba(82,42,37,0.4)]">
          {done ? (
            <div data-testid="lead-form-success" className="text-center py-10">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-sage/20 text-sage mb-5">
                <Check className="h-7 w-7" strokeWidth={2} />
              </div>
              <h3 className="font-magazine text-3xl text-sand-800 font-normal">Danke, wir melden uns!</h3>
              <p className="mt-3 text-sand-600">Du erhältst innerhalb von 24 h eine Rückmeldung per E-Mail oder Telefon.</p>
              <Button onClick={() => setDone(false)} className="mt-8 rounded-full bg-terra text-white hover:bg-terra-dark">
                Weitere Anfrage senden
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid={LEAD.form} className="space-y-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-terra mb-2">
                  Anfrage
                </div>
                <h3 className="font-magazine text-2xl md:text-[1.75rem] text-sand-800 font-normal leading-tight">Kostenlose Beratung anfragen</h3>
                <p className="text-sm text-sand-600 mt-2">Antwort innerhalb 24 h. Werktags meist schneller.</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lead-name" className="text-sand-700 text-xs uppercase tracking-wider font-semibold">Name *</Label>
                  <Input
                    id="lead-name"
                    data-testid={LEAD.name}
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    className="mt-1.5 h-12 bg-cream-100 border-cream-300 rounded-xl focus:border-sand-700"
                    placeholder="Vor- & Nachname"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lead-phone" className="text-sand-700 text-xs uppercase tracking-wider font-semibold">Telefon *</Label>
                  <Input
                    id="lead-phone"
                    data-testid={LEAD.phone}
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    className="mt-1.5 h-12 bg-cream-100 border-cream-300 rounded-xl focus:border-sand-700"
                    placeholder="0151 / 123 456 78"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="lead-email" className="text-sand-700 text-xs uppercase tracking-wider font-semibold">E-Mail *</Label>
                <Input
                  id="lead-email"
                  type="email"
                  data-testid={LEAD.email}
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  className="mt-1.5 h-12 bg-cream-100 border-cream-300 rounded-xl focus:border-sand-700"
                  placeholder="name@email.de"
                  required
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sand-700 text-xs uppercase tracking-wider font-semibold">Ich bin</Label>
                  <Select value={form.audience} onValueChange={(v) => update('audience', v)}>
                    <SelectTrigger aria-label="Ich bin" data-testid={LEAD.audience} className="mt-1.5 h-12 bg-cream-100 border-cream-300 rounded-xl">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pflegekraft">Pflegekraft / Privatperson</SelectItem>
                      <SelectItem value="pflegedienst">Pflegedienst / Einrichtung</SelectItem>
                      <SelectItem value="agentur">Agentur für Arbeit / Jobcenter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sand-700 text-xs uppercase tracking-wider font-semibold">Kursinteresse</Label>
                  <Select value={form.course_interest} onValueChange={(v) => update('course_interest', v)}>
                    <SelectTrigger aria-label="Kursinteresse" data-testid={LEAD.course} className="mt-1.5 h-12 bg-cream-100 border-cream-300 rounded-xl">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {['einstieg','erfahren','fuehrung','pflicht'].map((cat) => (
                        <div key={cat}>
                          <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-sand-500 font-semibold">{CATEGORY_LABELS[cat]}</div>
                          {COURSE_LIST.filter((c) => c.category === cat).map((c) => (
                            <SelectItem key={c.id} value={c.title}>{c.title}</SelectItem>
                          ))}
                        </div>
                      ))}
                      <SelectItem value="Ausbildung Pflegefachkraft (3 Jahre) – Partnerschule Mönchengladbach">
                        Ausbildung Pflegefachkraft (3 Jahre) – Partnerschule MG
                      </SelectItem>
                      <SelectItem value="Noch unsicher – bitte beraten">Noch unsicher – bitte beraten</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="lead-message" className="text-sand-700 text-xs uppercase tracking-wider font-semibold">Nachricht (optional)</Label>
                <Textarea
                  id="lead-message"
                  data-testid={LEAD.message}
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                  className="mt-1.5 bg-cream-100 border-cream-300 rounded-xl focus:border-sand-700 min-h-[100px]"
                  placeholder="Erzähl uns kurz, worum es geht."
                />
              </div>

              <div className="flex items-start gap-3 pt-1">
                <Checkbox
                  id="lead-consent"
                  data-testid={LEAD.consent}
                  checked={form.consent}
                  onCheckedChange={(v) => update('consent', !!v)}
                  className="mt-0.5"
                />
                <Label htmlFor="lead-consent" className="text-xs text-sand-600 leading-relaxed font-normal">
                  Ich willige ein, dass meine Angaben zur Bearbeitung meiner Anfrage verarbeitet werden. Mehr in der <a href="/datenschutz" className="underline">Datenschutzerklärung</a>. *
                </Label>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid={LEAD.submit}
                className="w-full h-14 rounded-full bg-terra text-white hover:bg-terra-dark text-base font-medium group"
              >
                {submitting ? 'Wird gesendet …' : (
                  <>
                    Kostenlose Beratung anfragen
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                  </>
                )}
              </Button>

              <p className="text-[11px] text-sand-500 text-center">100% unverbindlich · keine versteckten Kosten</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
